// Credits next to euros, always (docs/brand/BRAND.md, "Money is always visible"). The API reports model cost in
// USD; the server prices it: GET /v1/pricing says what a credit costs, the USD→EUR rate and our margin
// (core/api/src/routes/launch.ts). The app loads that once at start (`loadPricing`, `setPricing`) and every helper
// here reads it. Until it has loaded, or when it cannot be reached, the fallback below stands in. Pure apart from
// the one module-level cache.
import { num } from "../lib.ts";
import { loadWithFallback } from "./loadWithFallback.ts";

/** What the server answers on GET /v1/pricing, in the app's words. */
export interface Pricing {
  /** one credit in euros */
  creditEur: number;
  /** USD → EUR for model cost */
  usdEur: number;
  /** the share of a credit price that is our margin (0.15 = 15%) */
  margin: number;
  /** credits a new team starts with */
  starterCredits: number;
  /** the credit packs, with model cost and our margin shown apart */
  packs: PackPrice[];
  /** the hosted prices (F8): seats, the free version, sign-up; absent when the server does not say (self-host, F7) */
  hosted?: HostedPricing;
}

/** The F8 part of GET /v1/pricing. Prices are in euros, excl. VAT. */
export interface HostedPricing {
  /** one seat, per month */
  seatEur: number;
  /** credits that come with each seat, every month */
  seatCredits: number;
  /** unused plan credits roll over up to this many months' worth */
  rolloverMonths: number;
  seatStorageGb: number;
  /** storage above what the seats include, per GB per month */
  storageCreditsPerGb: number;
  /** the roles that take a seat; reviewers and viewers are free */
  seatRoles: string[];
  /** packs can only be bought with a plan */
  packsNeedPlan: boolean;
  free: { credits: number; productions: number; final: boolean; storageGb: number };
  /** "open": anyone can sign up; "code": an access code or invitation is needed */
  signup: "open" | "code";
  /** the Cloudflare Turnstile site key; null: no challenge on sign-up */
  turnstileSiteKey: string | null;
}

export interface PackPrice {
  credits: number;
  priceEur: number;
  modelEur: number;
  marginEur: number;
}

/** Used until the server's prices have loaded (the values the server ships with: ff.pricing). */
export const FALLBACK_PRICING: Pricing = {
  creditEur: 0.05,
  usdEur: 0.92,
  margin: 0.15,
  starterCredits: 500,
  packs: [],
};

let current: Pricing = FALLBACK_PRICING;

/** The prices in force: the server's once loaded, else the fallback. */
export const getPricing = (): Pricing => current;

/** Replaces the prices in force (app start, tests). */
export function setPricing(p: Pricing): void {
  current = p;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** The server's /pricing body as `Pricing`; null when it is not a usable price table. */
export function parsePricing(raw: unknown): Pricing | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const creditEur = num(r.credit_eur);
  const usdEur = num(r.usd_eur);
  const margin = num(r.margin);
  if (creditEur == null || creditEur <= 0 || usdEur == null || usdEur <= 0) return null;
  if (margin == null || margin < 0 || margin >= 1) return null;
  const packs: PackPrice[] = [];
  for (const p of Array.isArray(r.packs) ? r.packs : []) {
    const q = (p ?? {}) as Record<string, unknown>;
    const credits = num(q.credits);
    const priceEur = num(q.price_eur);
    if (credits == null || credits <= 0 || priceEur == null) continue;
    const marginEur = num(q.margin_eur) ?? round2(priceEur * margin);
    packs.push({
      credits,
      priceEur,
      marginEur,
      modelEur: num(q.model_cost_eur) ?? round2(priceEur - marginEur),
    });
  }
  const hosted = parseHosted(r);
  return {
    creditEur,
    usdEur,
    margin,
    starterCredits: num(r.starter_credits) ?? FALLBACK_PRICING.starterCredits,
    packs,
    ...(hosted ? { hosted } : {}),
  };
}

const SEAT_ROLES = ["owner", "producer", "director", "maker"];

/** The F8 fields of /pricing; null when the server has no seat price (it predates F8, or has no billing). */
function parseHosted(r: Record<string, unknown>): HostedPricing | null {
  const seatEur = num(r.seat_eur);
  const seatCredits = num(r.seat_credits);
  if (seatEur == null || seatEur < 0 || seatCredits == null || seatCredits < 0) return null;
  const f = (typeof r.free === "object" && r.free !== null ? r.free : {}) as Record<string, unknown>;
  const roles = Array.isArray(r.seat_roles)
    ? r.seat_roles.filter((x): x is string => typeof x === "string")
    : [];
  return {
    seatEur,
    seatCredits,
    rolloverMonths: num(r.rollover_months) ?? 2,
    seatStorageGb: num(r.seat_storage_gb) ?? 50,
    storageCreditsPerGb: num(r.storage_credits_per_gb) ?? 2,
    seatRoles: roles.length ? roles : SEAT_ROLES,
    packsNeedPlan: r.packs_need_plan !== false,
    free: {
      credits: num(f.credits) ?? 100,
      productions: num(f.productions) ?? 1,
      final: f.final === true,
      storageGb: num(f.storage_gb) ?? 5,
    },
    signup: r.signup === "open" ? "open" : "code",
    turnstileSiteKey:
      typeof r.turnstile_site_key === "string" && r.turnstile_site_key.trim()
        ? r.turnstile_site_key.trim()
        : null,
  };
}

/**
 * Loads the server's prices into force. Never rejects and never waits longer than `timeoutMs`: when the server
 * does not answer, the fallback stays. Answers what is in force afterwards.
 */
export function loadPricing(request: () => Promise<unknown>, timeoutMs = 3000): Promise<Pricing> {
  return loadWithFallback(request, parsePricing, getPricing, setPricing, "pricing", timeoutMs);
}

/**
 * What a model cost in USD comes to in credits: converted to euros, with the margin on top
 * (credits = usd × usd_eur / (1 − margin) / credit_eur, the server's ff.cost_to_credits).
 * An estimate rounds up (you never pay more than the button says), exact matches the server's two decimals,
 * and spend rounds to the nearest credit.
 */
export function creditsFromUsd(usd: unknown, mode: "estimate" | "exact" | "spent" = "spent"): number {
  const v = num(usd);
  if (v == null || v <= 0) return 0;
  const p = current;
  const credits = (v * p.usdEur) / (1 - p.margin) / p.creditEur;
  if (mode === "estimate") return Math.ceil(credits - 1e-9);
  return mode === "exact" ? round2(credits) : Math.round(credits);
}

export const eurOfCredits = (credits: number): number => round2(credits * current.creditEur);

const INT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const EXACT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const EUR = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Whole credits; under one credit its cents, so a small use never reads "0". */
export const creditsText = (credits: number): string =>
  credits > 0 && credits < 1 ? EXACT.format(credits) : INT.format(credits);
/** Credits with their cents ("21.65"), for the ledger where every row counts. */
export const creditsExact = (credits: number): string => EXACT.format(credits);
export const eurText = (eur: number): string => `€${EUR.format(eur)}`;

/** What one credit costs, for a sentence: "€0.05" (more digits only when the price needs them). */
export function creditPriceText(): string {
  const eur = current.creditEur;
  return Number.isInteger(Math.round(eur * 100 * 1e6) / 1e6) ? eurText(eur) : `€${String(eur)}`;
}

/** The credits as `creditsText` shows them: whole, except a use under one credit. Euros follow what is shown. */
export const shownCredits = (credits: number): number =>
  credits > 0 && credits < 1 ? Math.round(credits * 100) / 100 : Math.round(credits);

/** "24 cr · €1.20" (short, on buttons and cards) or "1,240 credits · €62.00" (long, in sentences and meters). */
/** What is left to spend, as shown: whole credits rounded down, so 499.57 left never reads as 500. */
export const leftCredits = (credits: number): number => Math.max(0, Math.floor(credits + 1e-9));

export function moneyText(credits: number, form: "short" | "long" = "short"): string {
  const shown = shownCredits(credits);
  const unit = form === "long" ? (shown === 1 ? "credit" : "credits") : "cr";
  return `${creditsText(shown)} ${unit} · ${eurText(eurOfCredits(shown))}`;
}

/** A USD amount from the API as credits and euros. */
export const usdMoneyText = (
  usd: unknown,
  form: "short" | "long" = "short",
  mode: "estimate" | "exact" | "spent" = "spent",
) => moneyText(creditsFromUsd(usd, mode), form);

/** The split of a credit pack price into model cost and our margin (p2-credits), from the margin in force. */
export function packSplit(priceEur: number): { modelEur: number; marginEur: number } {
  const marginEur = round2(priceEur * current.margin);
  return { modelEur: round2(priceEur - marginEur), marginEur };
}
